import _ from 'underscore';
import $ from 'jquery';
import { GridCustomizedPopupEditor } from '@bingads-webui-campaign/grid-customized-popup-editor';
import { CampaignuiBaseColumn } from '@bingads-webui-campaign/campaignui-base-column';
import { TrackingTemplateColumn, CustomParametersColumn, FinalUrlSuffixColumn } from '@bingads-webui-campaign/tracking-template-columns';
import conditionTemplate from '../../template/grid-column-condition.pug';
import flattenConditionTemplate from '../../template/grid-column-flatten-condition.pug';
import bidTemplate from '../../template/grid-column-bid.pug';
import warningColumnTemplate from '../../template/grid-column-warning.pug';
import adGroupNameTemplate from '../../template/grid-column-adgroup.pug';
import campaignNameTemplate from '../../template/grid-column-campaign.pug';
import { ProductGroupsEditBidsView } from './product-groups-edit-bid';
import { ProductGroupsEditBidBoostView } from './product-groups-edit-bid-boost';
import { ProductGroupPathUtil } from './product-groups-path-util';
import { getInlineEditableSaveFunction } from './product-groups-inline-edit';
import { productGroupLevel } from '../../constant';

const tableContainerSelector = '.product-groups-grid.bingadsgrid.grid';
const IsProductGroupBidLandscapeEnabled = deps => _.result(deps.config.get('Permissions'), 'IsProductGroupBidLandscapeEnabled');
const IsBidLessThan5CentsInShoppingCampaignEnabled = deps => _.result(deps.config.get('Permissions'), 'IsBidLessThan5CentsInShoppingCampaignEnabled');
const IsAvgPositionDeprecationPhase1Enabled = deps => _.result(deps.config.get('Permissions'), 'IsAvgPositionDeprecationPhase1Enabled');
const IsCurrentAccountInactive = deps => deps.config.get('CurrentAccountInActive');
const IsReadOnlyMode = deps => deps.config.get('ReadOnlyMode');
const NO_DATA_PLACEHOLDER_NONLEAF = '-';
const NO_DATA_PLACEHOLDER_LEAF = '';

const SORT_DIRECTION = [-1, 1];

export function shouldEnableEdit(enable, deps) {
  return !IsCurrentAccountInactive(deps) && !IsReadOnlyMode(deps) && enable;
}

export const productGroupSortIteratee = (item, direction) => {
  const condition = item.Condition;
  const isEveryThingElseRow = con => !con.Attribute && con.Operand;

  if (isEveryThingElseRow(condition)) {
    // No matter order, "Everything else in ..." is always in the bottom
    // '~' has a lower priority in ASCII order
    return direction === 1 ? '~' : '';
  }
  return item.Condition.Attribute;
};

const generatePickProperty = key => (item) => {
  if (item.isAggregateRow) {
    return NO_DATA_PLACEHOLDER_LEAF;
  }
  return item.IsLeaf ? item[key] || NO_DATA_PLACEHOLDER_LEAF : NO_DATA_PLACEHOLDER_NONLEAF;
};

// const emptyValueFormatter = (val, noData = NO_DATA_PLACEHOLDER_LEAF) =>
//   (_.isUndefined(val) || _.isNull(val) || _.isEmpty(val) ? noData : val);

const renderBidPopupEditor = (deps, bidEditor, myView, position) => {
  const popupEditor = new GridCustomizedPopupEditor({
    editorView: bidEditor,
    cellPosition: position,
    tableContainer: tableContainerSelector,
  });

  popupEditor.render();
};

class ProductGroupColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    super({
      name: 'ProductGroup',
      property: ['Condition', 'Id', 'ParentId', 'IsLeaf', 'IsExcluded', 'IsAutoBidding', 'IsBidLandscapeEnabled', 'ParentConditions', 'BidOption'],
      // Used in tree plugin
      title: deps.i18n.getString('ProductGroupsGrid_Column_ProductGroup'),
      sortable: productGroupSortIteratee,
    });
    this.deps = deps;
  }

  pickProperty(item) {
    return item;
  }

  template(item) {
    return conditionTemplate(_.extend({
      i18n: this.deps.i18n,
      showSelectorIcon: shouldEnableEdit((item.hasChildren || item.depth < 7), this.deps),
    }, _.pick(item, 'attributeText', 'hasChildren', 'isRoot', 'depth', 'isExpanded', 'total')));
  }
}

class ProductGroupFlattenColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    super({
      name: 'ProductGroup',
      property: ['Condition', 'Id', 'ParentId', 'IsLeaf', 'IsExcluded', 'IsAutoBidding', 'IsBidLandscapeEnabled', 'ParentConditions', 'BidOption'],
      // Used in tree plugin
      title: deps.i18n.getString('ProductGroupsGrid_Column_ProductGroup'),
      sortable: 'Condition/Attribute',
    });
    this.deps = deps;
    this.productGroupPathUtil = new ProductGroupPathUtil({ deps });
  }

  pickProperty(item) {
    return item;
  }

  template({ value }) {
    if (value.$total) {
      return flattenConditionTemplate(value);
    }
    const condition = { ...value.Condition };
    const parentConditions = value.ParentConditions;
    condition.Attribute = this.productGroupPathUtil.getDisplayName(condition, parentConditions);
    condition.Path = this.productGroupPathUtil.getPath(parentConditions);
    return flattenConditionTemplate(_.defaults(condition, { i18n: this.deps.i18n }));
  }
}

class CampaignNameColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    super({
      name: 'CampaignName',
      title: 'ProductGroupsGrid_Column_CampaignName',
      property: ['CampaignName', 'CampaignId'],
      i18n: deps.i18n,
      sortable: 'CampaignName',
    });
    this.cid = deps.pageContext.CurrentCustomer.Id;
    this.aid = deps.pageContext.CurrentAccount.Id;
  }

  get property() {
    return {
      get: (value) => {
        const {
          CampaignId: cmpid,
          CampaignName: name,
        } = value;

        const href = cmpid ? `#customer/${this.cid}/account/${this.aid}/campaign/${cmpid}/productgroups` : '#';

        return {
          name,
          href,
        };
      },
      key: this.config.property,
    };
  }

  get template() { return campaignNameTemplate; }
}

class AdGroupNameColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    super({
      name: 'AdGroupName',
      title: 'ProductGroupsGrid_Column_AdGroupName',
      property: ['AdGroupName', 'AdGroupId'],
      i18n: deps.i18n,
      sortable: 'AdGroupName',
    });
    this.cid = deps.pageContext.CurrentCustomer.Id;
    this.aid = deps.pageContext.CurrentAccount.Id;
    this.deps = deps;
  }

  get property() {
    return {
      get: (value) => {
        const name = value.AdGroupName;
        const adgid = value.AdGroupId;
        const currentCampaign = this.deps.pageContext.CurrentCampaign;
        // In campaign level, there is no CampaignId in row value
        const cmpid = value.CampaignId || (currentCampaign && currentCampaign.Id);

        const href = cmpid && adgid ? `#customer/${this.cid}/account/${this.aid}/campaign/${cmpid}/adgroup/${adgid}/productgroups` : '#';

        return {
          name,
          href,
        };
      },
      key: this.config.property,
    };
  }

  get template() { return adGroupNameTemplate; }
}

function getBidOption(bidOption) {
  if (bidOption == null) {
    return 'BidValue';
  }
  return bidOption;
}

class BidColumn extends CampaignuiBaseColumn {
  constructor({ deps, view }) {
    const title = deps.i18n.getString('ProductGroupsGrid_Column_Bid');

    super({
      name: 'Bid',
      title,
      property: 'BidAmount',
      sortable: {
        direction: SORT_DIRECTION,
        key: 'BidAmount/Bid/Amount',
      },
      helpId: 'Page_CampaignGrids_CurrentBid',
      headClasses: ['text-right'],
      bodyClasses: ['text-right'],
      enableEdit: item => shouldEnableEdit(getBidOption(item.BidOption) === 'BidValue' && item.IsLeaf && (item.IsLeaf || item.BidAmount.Bid.Amount != null), deps),
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.view = view;
    this.deps = deps;
  }

  get property() {
    return {
      get: item => item,
      key: 'BidAmount',
    };
  }

  template({ value }) {
    const showBidValue = value.BidAmount && value.BidAmount.Bid &&
     value.BidAmount.Bid.Amount != null && value.IsLeaf && !value.IsExcluded;
    const template = bidTemplate(value.isAggregateRow ? {
      isAggregateRow: true,
    } : {
      i18n: this.deps.i18n,
      isLeaf: value.IsLeaf,
      isExcluded: value.IsExcluded,
      // Only leaf node which is not excluded has valid bid
      bidString: showBidValue ? this.deps.decimal.fromFloatToString(value.BidAmount.Bid.Amount) : '',
      isBidLandscapeAvailable: IsProductGroupBidLandscapeEnabled(this.deps)
        && value.IsBidLandscapeEnabled,
      isAggregateRow: false,
      isAutoBidding: value.IsAutoBidding,
    });
    // if the template is empty string, the grid cell will show a value of '[object Object]'
    return getBidOption(value.BidOption) === 'BidValue' ? template : '<span></span>';
  }

  onPrompt({ position, item, event }) {
    if ($(event.target).is(`${tableContainerSelector} .bidlandscape_icon`)) {
      return;
    }
    const bidEditor = new ProductGroupsEditBidsView(_.extend(
      _.pick(item, 'BidAmount', 'IsExcluded', 'IsAutoBidding'),
      _.pick(this.view, 'campaignId', 'adgroupId'),
      {
        view: this.view,
        deps: this.deps,
        selectedItems: [item],
        isInlineEdit: true,
      }
    )).render();

    renderBidPopupEditor(this.deps, bidEditor, this.view, position);
  }
}

class BidBoostColumn extends CampaignuiBaseColumn {
  constructor({ deps, view }) {
    const title = deps.i18n.getString('ProductGroupsGrid_Column_BidBoost');

    super({
      name: 'BidBoost',
      title,
      property: 'BidBoost',
      sortable: {
        direction: SORT_DIRECTION,
        key: 'BidBoost/Multiplier',
      },
      helpId: 'pop_BA_BMC_BidBoost',
      headClasses: ['text-right'],
      bodyClasses: ['text-right'],
      enableEdit: item => shouldEnableEdit(getBidOption(item.BidOption) === 'BidBoost' && (item.IsLeaf && (item.IsExcluded || item.BidBoost.Multiplier != null)), this.deps),
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.deps = deps;
    this.view = view;
  }

  get property() {
    return {
      get: item => item,
      key: 'BidBoost',
    };
  }

  template({ value }) {
    const showBidBoostValue = value.BidBoost && value.BidBoost.Multiplier != null;
    const template = bidTemplate(value.isAggregateRow ? {
      isAggregateRow: true,
    } : {
      i18n: this.deps.i18n,
      isLeaf: value.IsLeaf,
      isExcluded: value.IsExcluded,
      bidString: showBidBoostValue ? this.deps.i18n.formatPercentToFixed(
        value.BidBoost.Multiplier / 100,
        { digits: 2 }
      ) : '',
      isBidLandscapeAvailable: false, // Bid landscape is not available for bid boost
      isAggregateRow: false,
    });
    // if the template is empty string, the grid cell will show a value of '[object Object]'
    return getBidOption(value.BidOption) === 'BidBoost' ? template : '<span></span>';
  }

  onPrompt({ position, item }) {
    const bidEditor = new ProductGroupsEditBidBoostView(_.extend(
      _.pick(item, 'BidBoost', 'IsExcluded'),
      _.pick(this.view, 'campaignId', 'adgroupId'),
      {
        view: this.view,
        deps: this.deps,
        selectedItems: [item],
        isInlineEdit: true,
      }
    )).render();

    renderBidPopupEditor(this.deps, bidEditor, this.view, position);
  }
}

class WarningsColumn extends CampaignuiBaseColumn {
  constructor({ deps, view }) {
    const title = deps.i18n.getString('ProductGroupsGrid_ColumnName_Warnings');

    super({
      name: 'Warnings',
      title,
      property: 'Warnings',
      helpId: 'pop_BA_ProductAds_BidAlerts',
      sortable: false,
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: title }),
    });
    this.deps = deps;
    this.view = view;
  }

  get property() {
    return {
      get: item => item,
    };
  }

  template({ value }) {
    const bidRangeKey = IsBidLessThan5CentsInShoppingCampaignEnabled(this.deps) ? 'ProductGroupBidRange' : 'KeywordBidRange';
    const MinimumBid = _.result(this.deps.config.get(bidRangeKey), 'FloorValue');
    const bidAmount = value.BidAmount && value.BidAmount.Bid ? value.BidAmount.Bid.Amount : null;
    const showBidBoostValue = value.BidBoost && value.BidBoost.Multiplier != null;
    return warningColumnTemplate(value.isAggregateRow ? {
      isAggregateRow: true,
    } : {
      i18n: this.deps.i18n,
      hasWarning: value.IsLeaf && !value.IsExcluded && bidAmount < MinimumBid && !showBidBoostValue,
      warningCount: 1,
    });
  }
}

class DestinationUrlColumn extends CampaignuiBaseColumn {
  constructor({ deps, view }) {
    const title = deps.i18n.getString('ProductGroupsGrid_Column_DestinationUrl');
    super({
      name: 'DestinationUrl',
      title,
      property: 'DestinationUrl',
      helpId: 'Page_CampaignGrids_DestinationUrl',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: title }),
      enableEdit: item => shouldEnableEdit(item.IsLeaf && !item.IsExcluded, deps),
      editorConfig: item =>
        ({
          i18n: deps.i18n,
          template: 'inline',
          fields: [{ property: 'DestinationUrl' }],
          saveModel: (model, editorView) => {
            getInlineEditableSaveFunction(deps)({
              view,
              i18n: deps.i18n,
              fieldName: 'DestinationUrl',
              updated: _.extend(model, _.pick(item, 'Id', 'CampaignId', 'AdGroupId')),
              editorView,
              editorViewCallbackAfterUpdate: () => {
                editorView.remove();
              },
            });
          },
        }),
    });
    this.deps = deps;
    this.view = view;
  }

  get pickProperty() {
    return generatePickProperty(this.config.property);
  }
}

class ProductGroupTrackingTemplateColumn extends TrackingTemplateColumn {
  constructor({ deps, view }) {
    super({
      i18n: deps.i18n,
      view,
      inlineEditableSave: getInlineEditableSaveFunction(deps),
    });
    this.config.enableEdit = item => shouldEnableEdit(item.IsLeaf && !item.IsExcluded, deps);
  }

  get pickProperty() {
    return generatePickProperty(this.config.property);
  }
}

class ProductGroupFinalUrlSuffixColumn extends FinalUrlSuffixColumn {
  constructor({ deps, view }) {
    super({
      i18n: deps.i18n,
      view,
      inlineEditableSave: getInlineEditableSaveFunction(deps),
    });
    this.config.enableEdit = item => shouldEnableEdit(item.IsLeaf && !item.IsExcluded, deps);
  }

  get pickProperty() {
    return generatePickProperty(this.config.property);
  }
}

class ProductGroupCustomParametersColumn extends CustomParametersColumn {
  constructor({ deps, view }) {
    super({
      i18n: deps.i18n,
      view,
      inlineEditableSave: getInlineEditableSaveFunction(deps),
      permissions: deps.config.get('Permissions'),
    });
    this.config.enableEdit = item => shouldEnableEdit(item.IsLeaf && !item.IsExcluded, deps);
  }

  get pickProperty() {
    return generatePickProperty(this.config.property);
  }
}

class PerfSpendColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_Spend');

    super({
      name: 'Spend',
      title: 'ProductGroupsGrid_Column_Spend',
      property: 'PerformanceMetrics/Spend',
      helpId: 'Page_CampaignGrids_Spend',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/Spend',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class PerfClicksColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_Clicks');

    super({
      name: 'Clicks',
      title: 'ProductGroupsGrid_Column_Clicks',
      property: 'PerformanceMetrics/Clicks',
      format: 'integer',
      helpId: 'Page_CampaignGrids_Clicks',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/Clicks',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class PerfTopImpressionRateColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_TopImpressionRate');

    super({
      name: 'TopImpressionRate',
      title: 'ProductGroupsGrid_Column_TopImpressionRate',
      property: 'PerformanceMetrics/TopImpressionRate',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_TopImpressionRate',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/TopImpressionRate',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class PerfAbsoluteTopImpressionRateColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_AbsoluteTopImpressionRate');

    super({
      name: 'AbsoluteTopImpressionRate',
      title: 'ProductGroupsGrid_Column_AbsoluteTopImpressionRate',
      property: 'PerformanceMetrics/AbsoluteTopImpressionRate',
      format: 'percentage-value',
      helpId: 'pop_BA_Reporting_Clarity_AbsoluteTopImpressionRate',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/AbsoluteTopImpressionRate',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class PerfImpressionsColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_Impressions');

    super({
      name: 'Impressions',
      title: 'ProductGroupsGrid_Column_Impressions',
      property: 'PerformanceMetrics/Impressions',
      format: 'integer',
      helpId: 'Page_CampaignGrids_Impr',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/Impressions',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class PerfCTRColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_CTR');

    super({
      name: 'CTR',
      title: 'ProductGroupsGrid_Column_CTR',
      property: 'PerformanceMetrics/CTR',
      format: 'percentage-value',
      helpId: 'Page_CampaignGrids_CTR',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/CTR',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class PerfAverageCPCColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_CostPerConversion');

    super({
      name: 'AverageCPC',
      title: 'ProductGroupsGrid_Column_CostPerConversion',
      property: 'PerformanceMetrics/AverageCPC',
      helpId: 'Page_CampaignGrids_AvgCPC',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/AverageCPC',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class PerfAverageCPMColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_CostPerImpression');

    super({
      name: 'AverageCPM',
      title: 'ProductGroupsGrid_Column_CostPerImpression',
      property: 'PerformanceMetrics/AverageCPM',
      helpId: 'Page_CampaignGrids_AvgCPM',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/AverageCPM',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class ConversionsColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_Conversions');

    super({
      name: 'Conversions',
      title: 'ProductGroupsGrid_Column_Conversions',
      property: 'PerformanceMetrics/Conversions',
      helpId: 'Page_CampaignGrids_Conv',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/Conversions',
      },
      format: 'integer',
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class ConversionRateColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_ConversionRate');

    super({
      name: 'ConversionRate',
      title: 'ProductGroupsGrid_Column_ConversionRate',
      property: 'PerformanceMetrics/ConversionRate',
      helpId: 'Page_CampaignGrids_ConvRate',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/ConversionRate',
      },
      format: 'percentage-value',
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class RevenueOnAdSpendColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_RevenueOnAdSpend');

    super({
      name: 'RevenueOnAdSpend',
      title: 'ProductGroupsGrid_Column_RevenueOnAdSpend',
      property: 'PerformanceMetrics/RevenueOnAdSpend',
      helpId: 'Page_CampaignGrids_ReturnAdSpend',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/RevenueOnAdSpend',
      },
      format: 'percentage-value',
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class AdvertiserReportedRevenueColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_AdvertiserReportedRevenue');

    super({
      name: 'AdvertiserReportedRevenue',
      title: 'ProductGroupsGrid_Column_AdvertiserReportedRevenue',
      property: 'PerformanceMetrics/AdvertiserReportedRevenue',
      helpId: 'Page_CampaignGrids_Revenue',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/RevenueOnAdSpend',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class CPAColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_CPA');

    super({
      name: 'CPA',
      title: 'ProductGroupsGrid_Column_CPA',
      property: 'PerformanceMetrics/CPA',
      helpId: 'Page_CampaignGrids_CPA',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/CPA',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class AllConversionsColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_AllConversions');

    super({
      name: 'AllConversions',
      title: 'ProductGroupsGrid_Column_AllConversions',
      property: 'PerformanceMetrics/AllConversions',
      helpId: 'pop_BA_CampaignGrids_AllConv',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/AllConversions',
      },
      format: 'integer',
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class AllConversionRateColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_AllConversionRate');

    super({
      name: 'AllConversionRate',
      title: 'ProductGroupsGrid_Column_AllConversionRate',
      property: 'PerformanceMetrics/AllConversionRate',
      helpId: 'pop_BA_CampaignGrids_AllConvRate',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/AllConversionRate',
      },
      format: 'percentage-value',
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class AllConversionRevenueOnAdSpendColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_AllConversionRevenueOnAdSpend');

    super({
      name: 'AllConversionRevenueOnAdSpend',
      title: 'ProductGroupsGrid_Column_AllConversionRevenueOnAdSpend',
      property: 'PerformanceMetrics/AllConversionRevenueOnAdSpend',
      helpId: 'pop_BA_CampaignGrids_AllConvROAS',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/AllConversionRevenueOnAdSpend',
      },
      format: 'percentage-value',
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class AllConversionAdvertiserReportedRevenueColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_AllConversionAdvertiserReportedRevenue');

    super({
      name: 'AllConversionAdvertiserReportedRevenue',
      title: 'ProductGroupsGrid_Column_AllConversionAdvertiserReportedRevenue',
      property: 'PerformanceMetrics/AllConversionAdvertiserReportedRevenue',
      helpId: 'pop_BA_CampaignGrids_AllConvRevenue',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/AllConversionAdvertiserReportedRevenue',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class AllConversionCPAColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_AllConversionCPA');

    super({
      name: 'AllConversionCPA',
      title: 'ProductGroupsGrid_Column_AllConversionCPA',
      property: 'PerformanceMetrics/AllConversionCPA',
      helpId: 'pop_BA_CampaignGrids_AllConvCPA',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'PerformanceMetrics/AllConversionCPA',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class ProductsSubmittedColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_ProductsSubmitted');

    super({
      name: 'ProductsSubmitted',
      title: 'ProductGroupsGrid_Column_ProductsSubmitted',
      property: 'SubmittedOfferCount',
      format: 'integer',
      helpId: 'pop_BA_BSC_ProductsSubmitted',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'SubmittedOfferCount',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

class ProductsTargetedColumn extends CampaignuiBaseColumn {
  constructor({ deps }) {
    const localizedTitle = deps.i18n.getString('ProductGroupsGrid_Column_ProductsTargeted');

    super({
      name: 'ProductsTargeted',
      title: 'ProductGroupsGrid_Column_ProductsTargeted',
      property: 'TargetedOfferCount',
      format: 'integer',
      helpId: 'pop_BA_BSC_ProductTargeted',
      i18n: deps.i18n,
      sortable: {
        direction: SORT_DIRECTION,
        key: 'TargetedOfferCount',
      },
      type: 'number',
      helpAriaLabel: deps.i18n.getString('Click to learn more about {{item}}', { item: localizedTitle }),
    });
  }
}

function getAttributeColumns({
  view,
  isTreeView,
}) {
  const { isBidValueOption, isBidBoostOption } = view;
  return _.compact([
    isTreeView ? ProductGroupColumn : ProductGroupFlattenColumn,
    view.level === productGroupLevel.Account ? CampaignNameColumn : null,
    view.level === productGroupLevel.Account || view.level === productGroupLevel.Campaign ?
      AdGroupNameColumn : null,
    isBidValueOption ? BidColumn : null,
    isBidBoostOption ? BidBoostColumn : null,
    WarningsColumn,
    DestinationUrlColumn,
    ProductGroupTrackingTemplateColumn,
    ProductGroupCustomParametersColumn,
    ProductGroupFinalUrlSuffixColumn,
  ]);
}

function getPerfColumns(deps) {
  return _.compact([
    PerfSpendColumn,
    PerfClicksColumn,
    PerfImpressionsColumn,
    PerfCTRColumn,
    PerfAverageCPCColumn,
    PerfAverageCPMColumn,
    IsAvgPositionDeprecationPhase1Enabled(deps) && PerfTopImpressionRateColumn,
    IsAvgPositionDeprecationPhase1Enabled(deps) && PerfAbsoluteTopImpressionRateColumn,
  ]);
}

function getConvColumns() {
  return _.compact([
    ConversionsColumn,
    ConversionRateColumn,
    RevenueOnAdSpendColumn,
    AdvertiserReportedRevenueColumn,
    CPAColumn,
    AllConversionsColumn,
    AllConversionRateColumn,
    AllConversionRevenueOnAdSpendColumn,
    AllConversionAdvertiserReportedRevenueColumn,
    AllConversionCPAColumn,
  ]);
}

function getShoppingColumns() {
  return [
    ProductsSubmittedColumn,
    ProductsTargetedColumn,
  ];
}

export function getGridColumnsConfig(deps, view, isTreeView) {
  return _.chain([
    ...getAttributeColumns({ deps, view, isTreeView }),
    ...getPerfColumns(deps),
    ...getConvColumns(deps),
    ...getShoppingColumns(),
  ])
    .compact()
    .map(Column => (new Column({ deps, view }).toJSON()))
    .value();
}

function getCategoryOptions(options, deps, view) {
  return _.map(options, (option) => {
    const column = new option.Column({
      deps,
      view,
    });

    return {
      key: column.name,
      name: column.title,
      selectOption: option.selectOption,
    };
  });
}

function getColumnNames(Cols, deps, view) {
  return _.map(Cols, Column => new Column({
    deps,
    view,
  }).name);
}

function getLockedColumns(categories) {
  let lockedColumns = [];
  _.forEach(categories, (category) => {
    lockedColumns = lockedColumns.concat(_.map(_.filter(category.options, option => option.selectOption === 'locked'), value => value.Column));
  });

  return lockedColumns;
}

export function getColumnChooserPluginConfig(deps, view, isTreeView) {
  const isLockedColumn = (Column) => {
    const lockColumnWhiteList = [
      ProductGroupColumn,
      ProductGroupFlattenColumn,
      BidColumn,
      BidBoostColumn,
      WarningsColumn,
      AdGroupNameColumn,
      CampaignNameColumn,
    ];
    return _.contains(lockColumnWhiteList, Column) ? 'locked' : 'optional';
  };

  const categories = [{
    name: deps.i18n.getString('Attributes'),
    options: _.compact(_.map(getAttributeColumns({ deps, view, isTreeView }), Column => ({
      Column,
      selectOption: isLockedColumn(Column),
    }))),
  }, {
    name: deps.i18n.getString('Performance'),
    options: _.map(getPerfColumns(deps), Column => ({
      Column,
      selectOption: isLockedColumn(Column),
    })),
  }, {
    name: deps.i18n.getString('Conversions'),
    options: _.map(getConvColumns(), Column => ({
      Column,
      selectOption: isLockedColumn(Column),
    })),
  }, {
    name: deps.i18n.getString('ColumnCategory_Shopping'),
    options: _.map(getShoppingColumns(), Column => ({
      Column,
      selectOption: isLockedColumn(Column),
    })),
  }];

  return {
    categories: _.map(categories, category => ({
      name: category.name,
      options: getCategoryOptions(category.options, deps, view),
    })),
    selected: getColumnNames([
      ...getAttributeColumns({ deps, view }),
      ...getPerfColumns(deps),
      ...getConvColumns(),
      ...getShoppingColumns(),
    ], deps, view),
    locked: getColumnNames(getLockedColumns(categories), deps, view),
    patchQuery: true,
    Columns: deps.Columns,
    SelectedColumns: deps.SelectedColumns,
    LegacyColumns: deps.LegacyColumns,
    LegacySelectedColumns: deps.LegacySelectedColumns,
    i18n: deps.i18n,
  };
}
